import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { DiscoverCard } from '../components/DiscoverCard';
import { useNearbyLikeProfiles, Profile, useSelfProfile } from '@/services/profiles';
import { useGeolocation } from '@/hooks/useGeolocation';
import { useLocation } from '@/providers/LocationProvider';
import { useTheme } from '@/theme/ThemeProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useUiConfig } from '@/services/config';
import { showNotification } from '@/utils/notifications';
import styles from './DiscoverPage.module.css';

interface CachedProfile
  extends Pick<
    Profile,
    | 'id'
    | 'name'
    | 'shortid'
    | 'interests'
    | 'description'
    | 'metadata'
    | 'location'
    | 'preferredDistanceUnit'
  > {}

const CACHE_KEY = 'discover.cache.v1';

const loadCachedProfiles = (): CachedProfile[] => {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed as CachedProfile[];
  } catch (error) {
    console.warn('Failed to load cached discovery profiles', error);
    return [];
  }
};

const persistCachedProfiles = (profiles: Profile[]) => {
  if (typeof window === 'undefined' || profiles.length === 0) {
    return;
  }
  const payload: CachedProfile[] = profiles.map((profile) => ({
    id: profile.id,
    name: profile.name,
    shortid: profile.shortid,
    interests: profile.interests,
    description: profile.description,
    metadata: profile.metadata,
    location: profile.location,
    preferredDistanceUnit: profile.preferredDistanceUnit,
  }));
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(payload.slice(0, 25)));
  } catch (error) {
    console.warn('Unable to persist discovery cache', error);
  }
};

const DEFAULT_INTEREST_LIMIT = 20;

const aggregateTopInterests = async (profiles: Profile[], limit: number) => {
  const counts = new Map<string, number>();
  profiles.forEach((profile) => {
    (profile.interests ?? []).forEach((interest) => {
      const normalized = interest.trim();
      if (!normalized) {
        return;
      }
      counts.set(normalized, (counts.get(normalized) ?? 0) + 1);
    });
  });

  return Array.from(counts.entries())
    .sort((a, b) => {
      if (b[1] !== a[1]) {
        return b[1] - a[1];
      }
      return a[0].localeCompare(b[0]);
    })
    .slice(0, limit)
    .map(([interest]) => interest);
};

const normalizeUnit = (unit?: string): 'm' | 'ft' => (unit === 'ft' ? 'ft' : 'm');

const DiscoverPage = () => {
  const navigate = useNavigate();
  const geolocation = useGeolocation();
  const location = useLocation();
  const { autoPalette, locationEnabled } = useTheme();
  const { user } = useAuth();
  const { data: config } = useUiConfig();
  const [interestFilter, setInterestFilter] = useState<string>('all');
  const [queue, setQueue] = useState<Profile[]>([]);
  const [interestOptions, setInterestOptions] = useState<string[]>(['all']);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const previousMatchCountRef = useRef<number>(0);
  const cachedProfiles = useMemo(loadCachedProfiles, []);

  const { data: viewerProfile } = useSelfProfile({
    enabled: Boolean(user),
  });
  const viewerProfileId = (viewerProfile as Profile)?.id;

  const nearbyLikePayload = useMemo(() => {
    const profile = viewerProfile as Profile;
    if (!profile?.location) {
      return null;
    }
    return {
      profile: {
        id: profile.id,
        location: profile.location,
        searchDescription: profile.searchDescription || profile.description || '',
      },
      limit: 50,
    };
  }, [viewerProfile]);

  const { data, isLoading, isError, refetch, isFetching } = useNearbyLikeProfiles(
    nearbyLikePayload || {
      profile: { id: '', location: { latitude: 0, longitude: 0 }, searchDescription: '' },
      limit: 50,
    },
  );

  useEffect(() => {
    if (data?.length) {
      const sanitized = viewerProfileId
        ? data.filter((profile) => profile.id !== viewerProfileId)
        : data;
      persistCachedProfiles(sanitized);
    }
  }, [data, viewerProfileId]);

  // Notify when new matches arrive while the app is open
  useEffect(() => {
    if (!data || !data.length) {
      previousMatchCountRef.current = 0;
      return;
    }
    const previous = previousMatchCountRef.current;
    const next = data.length;
    if (next > previous && previous !== 0) {
      const newCount = next - previous;
      showNotification('New matches available', {
        body:
          newCount === 1
            ? '1 new match found nearby.'
            : `${newCount} new matches found nearby.`,
        tag: 'discover-matches',
      }).catch((error) => {
        console.warn('Unable to show match notification', error);
      });
    }
    previousMatchCountRef.current = next;
  }, [data]);

  const profiles = useMemo<Profile[]>(() => {
    const source = data?.length ? data : (cachedProfiles as Profile[]);

    if (!viewerProfileId) {
      return source;
    }

    return source.filter((profile) => profile.id !== viewerProfileId);
  }, [cachedProfiles, data, viewerProfileId]);

  const filteredProfiles = useMemo(() => {
    if (interestFilter === 'all') {
      return profiles;
    }
    return profiles.filter((profile) => profile.interests?.includes(interestFilter));
  }, [interestFilter, profiles]);

  const interestLimit = config?.discover?.maxInterestFilters ?? DEFAULT_INTEREST_LIMIT;

  useEffect(() => {
    let cancelled = false;

    // Get current user's interests for comparison
    const currentUserInterests = new Set((viewerProfile as Profile)?.interests ?? []);

    // Only include interests that both the current user has AND other users have
    const sharedInterestCounts = new Map<string, number>();

    profiles.forEach((profile) => {
      (profile.interests ?? []).forEach((interest) => {
        const normalized = interest.trim();
        if (!normalized || !currentUserInterests.has(normalized)) {
          return; // Skip if current user doesn't have this interest
        }
        // Count shared interests (current user has it + other user has it)
        sharedInterestCounts.set(
          normalized,
          (sharedInterestCounts.get(normalized) ?? 0) + 1,
        );
      });
    });

    // Add +1 to each shared interest to account for current user having it too
    const finalCounts = Array.from(sharedInterestCounts.entries())
      .map(([interest, count]) => [interest, count + 1] as [string, number])
      .sort((a, b) => {
        if (b[1] !== a[1]) {
          return b[1] - a[1];
        }
        return a[0].localeCompare(b[0]);
      })
      .slice(0, interestLimit)
      .map(([interest]) => interest);

    if (!cancelled) {
      setInterestOptions(['all', ...finalCounts]);
    }

    return () => {
      cancelled = true;
    };
  }, [profiles, interestLimit, (viewerProfile as Profile)?.interests]);

  useEffect(() => {
    if (interestFilter === 'all') {
      return;
    }
    if (!interestOptions.includes(interestFilter)) {
      setInterestFilter('all');
    }
  }, [interestFilter, interestOptions]);

  useEffect(() => {
    setQueue(filteredProfiles.slice(0, 20));
  }, [filteredProfiles]);

  const distanceUnit = useMemo(
    () =>
      normalizeUnit(
        (viewerProfile as Profile)?.preferredDistanceUnit ??
          config?.preferredDistanceUnit,
      ),
    [(viewerProfile as Profile)?.preferredDistanceUnit, config?.preferredDistanceUnit],
  );

  const handlePass = useCallback((profile: Profile) => {
    setQueue((prev) => prev.filter((candidate) => candidate.id !== profile.id));
  }, []);

  const handleLike = useCallback(
    (profile: Profile) => {
      // Use email as the user identifier to match what the auth system uses
      const chatUserId = profile.email || profile.id;
      navigate(`/chat/${chatUserId}`);
    },
    [navigate],
  );

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);

    try {
      // First, try to update the current location if possible
      if (location.forceLocationUpdate) {
        try {
          await location.forceLocationUpdate();
          console.info('[DiscoverPage] Location updated before refresh');
        } catch (error) {
          console.warn('[DiscoverPage] Failed to update location before refresh', error);
          // Continue with refresh even if location update fails
        }
      }

      // Then refresh the nearby profiles
      await refetch();
      console.info('[DiscoverPage] Profiles refreshed');
    } catch (error) {
      console.error('[DiscoverPage] Refresh failed', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [location.forceLocationUpdate, refetch]);

  const statusMessage = useMemo(() => {
    if (isError) {
      return 'Unable to reach the profiles service right now.';
    }
    if (isRefreshing) {
      return 'Updating location and refreshing matches…';
    }
    if (isFetching) {
      return 'Refreshing matches…';
    }
    if (!queue.length) {
      return 'You are all caught up. Come back soon for more campus or nearby matches!';
    }
    return `${queue.length} profiles ready to explore`;
  }, [isError, isRefreshing, isFetching, queue.length]);

  const deckContent = useMemo(() => {
    if (isLoading && !cachedProfiles.length) {
      return <div className={styles.placeholder}>Loading profiles…</div>;
    }
    if (queue.length) {
      return queue.map((profile) => (
        <DiscoverCard
          key={profile.id}
          profile={profile}
          onPass={handlePass}
          onLike={handleLike}
          distanceUnit={distanceUnit}
          viewerLocation={(viewerProfile as Profile)?.location}
        />
      ));
    }
    return (
      <div className={styles.placeholder}>
        <h2>No profiles to show right now</h2>
        <p>Adjust your interests or check back later for fresh connections.</p>
      </div>
    );
  }, [
    cachedProfiles.length,
    handleLike,
    handlePass,
    isLoading,
    queue,
    distanceUnit,
    (viewerProfile as Profile)?.location,
  ]);

  return (
    <div className={styles.page}>
      <Helmet>
        <title>Discover • AwkHerd</title>
      </Helmet>
      <header className={styles.header}>
        <div>
          <h1>Discover</h1>
          <p>
            {locationEnabled && autoPalette
              ? `Using ${autoPalette.name ?? autoPalette.paletteId ?? 'nearby'} theme for your campus/area.`
              : 'Browse curated profiles — partners get campus flavor, and anyone can connect from anywhere.'}
          </p>
        </div>
        <div className={styles.headerMeta}>
          <span className={styles.status} aria-live="polite">
            {statusMessage}
          </span>
          <button
            type="button"
            className={styles.refresh}
            onClick={handleRefresh}
            disabled={isRefreshing || isFetching}
          >
            {isRefreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </header>

      <section className={styles.filters} aria-label="Interest filters">
        {interestOptions.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setInterestFilter(option)}
            className={
              option === interestFilter ? styles.filterActive : styles.filterButton
            }
          >
            {option === 'all' ? 'All interests' : option}
          </button>
        ))}
      </section>

      {geolocation.status === 'denied' && (
        <p className={styles.notice}>
          Location access is disabled. Results are based on your campus or saved preferences instead.
        </p>
      )}

      <section className={styles.deck} aria-live="polite">
        {deckContent}
      </section>
    </div>
  );
};

export default DiscoverPage;
